import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';

import { API } from '@/lib/apiBase';

/**
 * Bulk photo actions (delete / reprice / reassign) with shared loading + toast handling.
 *
 * Returns { bulkSaving, bulkDelete, bulkReprice, bulkReassign }.
 * All handlers accept the ID list explicitly so the hook stays state-free.
 */
export const useBulkPhotoActions = ({ onSuccess } = {}) => {
  const { getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [bulkSaving, setBulkSaving] = useState(false);

  const _postBulk = async (path, body, successKey) => {
    setBulkSaving(true);
    try {
      const res = await fetch(`${API}${path}`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Bulk request failed');
      const result = await res.json();
      const n = result.deleted ?? result.updated ?? 0;
      toast.success(t(successKey).replace('{n}', n));
      if (onSuccess) onSuccess();
      return result;
    } catch (error) {
      toast.error(t('common.error'));
      return null;
    } finally {
      setBulkSaving(false);
    }
  };

  const bulkDelete = async (ids) => {
    if (ids.length === 0) return null;
    const confirmMsg = t('admin.bulkDeleteConfirm').replace('{n}', ids.length);
    if (!window.confirm(confirmMsg)) return null;
    return _postBulk('/photos/bulk-delete', { photo_ids: ids }, 'admin.bulkDeleteSuccess');
  };

  const bulkReprice = async (ids, newPrice) => {
    if (ids.length === 0) return null;
    return _postBulk('/photos/bulk-update', { photo_ids: ids, price: newPrice }, 'admin.bulkUpdateSuccess');
  };

  const bulkReassign = async (ids, payload) => {
    if (ids.length === 0) return null;
    return _postBulk('/photos/bulk-update', { photo_ids: ids, ...payload }, 'admin.bulkUpdateSuccess');
  };

  return { bulkSaving, bulkDelete, bulkReprice, bulkReassign };
};
